import { Button, InputGroup, SelectGroup } from '@/common/components'
import useI18n from '@/shared/hooks/usei18n'
import { useMemo, useState } from 'react'
import { GoodsFamily } from '@/common'
import {
  InstallationFormData,
  installationDetailsSchema
} from '../../pages/supplier-details/components/InstallationForm.schema'
import z from 'zod'

type StateSchema = z.infer<typeof installationDetailsSchema>

type Props = {
  data: InstallationFormData
  onNextClick: (data: StateSchema) => void
  onBackClick: () => void
}

export default function InstallationDetailsStep({ data, onNextClick, onBackClick }: Props) {
  const { t } = useI18n()

  const goodsFamilyOptions: {
    value: GoodsFamily
    label: string
  }[] = useMemo(() => {
    return Object.values(GoodsFamily).map(goodsFamily => ({
      value: goodsFamily,
      label: t(`common:goodsFamily.${goodsFamily}`)
    }))
  }, [t])

  const [state, setState] = useState<StateSchema>({
    name: data.name,
    goodsFamily: data.goodsFamily as GoodsFamily
  })

  const { name, goodsFamily } = state

  const validation = installationDetailsSchema.safeParse(state)

  const handleNext = () => {
    onNextClick(state)
  }

  const handleNameChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, name: evt.target.value })
  }

  return (
    <div className="space-y-6 pb-2">
      <h2 className="text-xl font-semibold mb-2">
        {t('installations:installationInputFields.sections.installationDetails')}
      </h2>
      <p className="text-sm text-text-secondary mb-6">
        {t('installations:createInstallationDialog.steps.descriptions.details')}
      </p>
      <div className="space-y-4">
        <div className="flex-1">
          <InputGroup
            label={t('installations:installationInputFields.name.label')}
            required
            value={name}
            onChange={handleNameChange}
          />
        </div>
        <div className="flex-1">
          <SelectGroup
            options={goodsFamilyOptions}
            required
            tooltip={t('installations:installationInputFields.economicActivity.tooltip')}
            label={t('installations:installationInputFields.economicActivity.label')}
            value={goodsFamily}
            onChange={evt => setState({ ...state, goodsFamily: evt.target.value as GoodsFamily })}
          />
        </div>
      </div>
      <div className="flex justify-between pt-16">
        <Button variant="neutral" onClick={onBackClick}>
          {t('common:actions.back')}
        </Button>
        <Button onClick={handleNext} disabled={!validation.success}>
          {t('common:actions.next')}
        </Button>
      </div>
    </div>
  )
}
